import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import CollapsableImageContent from "../components/CollapsableImageContent";
import FullscreenModal from "../components/FullscreenModal";
import Icon from "../components/Icon";
import LandFilters from "../components/LandFilters";
import MoreDetailsBtn from "../components/MoreDetailsBtn";
import SearchResultsBar from "../components/SearchResultsBar";
import Selectable from "../components/Selectable";
import { ApiContext, NavigationContext, UserContext } from "../contexts";
import useWindowSize from "../hooks/useWindowSize";
import { HouseLand, IconName, LandType, RoutePath } from "../types";
import { scrollToTop } from "../util";

const PackageSearchResultsScreen = observer(() => {
  const userContext = useContext(UserContext);
  const apiContext = useContext(ApiContext);
  const navContext = useContext(NavigationContext);

  const windowSize = useWindowSize();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);

  const debouncedGetHouseLandFromApi = useDebouncedCallback(() => {
    apiContext.getHouseLand();
  }, 1000);

  useEffect(() => {
    scrollToTop();
    navContext.resetNextBtn();
    navContext.setNextButtonEnabled(false);
    navContext.resetOnNavigateBack();

    navContext.setOnNavigateBack(() => {
      userContext.setSelectedHouseLand(null);
      navContext.setSubtotalVisible(false);
    });

    return () => {
      // Unbind the handler when we unmount
      navContext.resetOnNavigateBack();
    };
  }, []);

  useEffect(() => {
    // When search filters change, debounce search for new homes
    apiContext.setIsLoading(true);
    userContext.setSelectedHouseLand(null);
    debouncedGetHouseLandFromApi();
  }, [
    apiContext.houseType,
    apiContext.region,
    apiContext.estate,
    apiContext.numBeds,
    apiContext.numBaths,
    apiContext.numCars,
    apiContext.numLiving,
    apiContext.minBudget,
    apiContext.maxBudget,
    apiContext.minLotSize,
    apiContext.maxLotSize,
    apiContext.sortMethod,
  ]);

  useEffect(() => {
    handleFooterNavVisibilty();
  }, [userContext.selectedHouseLand, userContext.subtotalPrice]);

  const handleFooterNavVisibilty = () => {
    navContext.setNextButtonEnabled(userContext.selectedHouseLand !== null);
    navContext.setSubtotalVisible(
      userContext.selectedHouseLand !== null && userContext.subtotalPrice > 0
    );
  };

  const onChangeRegion = (regionId: string) => {
    if (apiContext.region !== null && apiContext.region.id !== regionId) {
      apiContext.setEstate(null);
    }
    if (apiContext.regionsKeyedById[regionId] !== undefined) {
      apiContext.setRegion(apiContext.regionsKeyedById[regionId]);
    } else {
      apiContext.setRegion(null);
    }
  };

  const onChangeEstate = (estateId: string) => {
    if (apiContext.region !== null && estateId !== "other") {
      apiContext.setEstateOther("");
      for (const estate of apiContext.region.estates) {
        if (estate.id === estateId) {
          apiContext.setEstate(estate);
        }
      }
    } else if (estateId === "other") {
      apiContext.setEstate({
        id: "other",
        title: "Other",
        site_cost: null,
      });
    }
  };

  const onChangeMinMetres = (val: string) => {
    if (val === "") {
      apiContext.setMinLotSize(null);
    } else {
      apiContext.setMinLotSize(parseInt(val, 10));
    }
  };

  const onChangeMaxMetres = (val: string) => {
    if (val === "") {
      apiContext.setMaxLotSize(null);
    } else {
      apiContext.setMaxLotSize(parseInt(val, 10));
    }
  };

  const onResetFilters = () => {
    apiContext.setRegion(null);
    apiContext.setEstate(null);
    apiContext.setMinLotSize(null);
    apiContext.setMaxLotSize(null);
  };

  const onClickHouseLand = (item: HouseLand) => {
    userContext.setSelectedHouseLand(item);
  };

  const onClickMoreDetails = (
    item: HouseLand,
    e?: React.MouseEvent<HTMLElement>
  ) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    userContext.setSelectedHouseLand(item);
    navContext.goTo(RoutePath.PACKAGE_DETAILS);
  };

  const onLandFiltersSearch = () => {
    setIsModalClosing(true);
  };

  const onCloseSearchModal = () => {
    setIsModalOpen(false);
    setIsModalClosing(false);
  };

  return (
    <div className={"screen screen-package-search-results"}>
      <div className={"container"}>
        <h2 className="h1">
          House & Land Packages{" "}
          {apiContext.region ? `in ${apiContext.region.title}` : ""}{" "}
        </h2>
        <p className={"subtitle"}>
          Here is a selection of house and land packages that meet your
          requirements.
        </p>
      </div>
      {windowSize.width > 992 ? (
        <div className={"filters-desktop container"}>
          <p className={"section-label"}>Refine Your Search</p>
          <LandFilters
            suburbs={apiContext.regionResults}
            houseSizes={apiContext.houseSizesResults}
            suburb={apiContext.region ? apiContext.region.id : ""}
            estate={apiContext.estate ? apiContext.estate.id : ""}
            minMetres={
              apiContext.minLotSize !== null
                ? apiContext.minLotSize.toString(10)
                : ""
            }
            maxMetres={
              apiContext.maxLotSize !== null
                ? apiContext.maxLotSize.toString(10)
                : ""
            }
            onChangeSuburb={onChangeRegion}
            onChangeEstate={onChangeEstate}
            onChangeMinMetres={onChangeMinMetres}
            onChangeMaxMetres={onChangeMaxMetres}
            onReset={onResetFilters}
          />
        </div>
      ) : (
        <div
          className={"btn btn-secondary filters-mobile"}
          onClick={() => setIsModalOpen(true)}
        >
          Refine Your Search
          <Icon name={IconName.CHEVRON_DOWN} />
        </div>
      )}

      <div className={"container results-container"}>
        <SearchResultsBar landType={LandType.HOUSE_LAND_PACKAGE} />
        {apiContext.isLoading ? (
          <div />
        ) : apiContext.houseLandSearchResults.length > 0 ? (
          <div className={"house-land-results"}>
            {apiContext.houseLandSearchResults.map((item) => {
              return (
                <div key={item.id} className={"house-land-result"}>
                  <Selectable
                    insetTick={windowSize.width < 992}
                    selected={
                      userContext.selectedHouseLand
                        ? userContext.selectedHouseLand.id === item.id
                        : false
                    }
                  >
                    <>
                      <CollapsableImageContent
                        layout={"house_land"}
                        fixed_price={item.pdf_fixed_price}
                        image={item.listing_image}
                        title={item.address}
                        address={`${item.suburb} ${item.state} ${item.postcode}`}
                        price={item.price}
                        selected={false}
                        selectableHidden={true}
                        onClick={() => onClickHouseLand(item)}
                      >
                        <div className={"house-land-details"}>
                          <div className={"details"}>
                            <div className={"details-row"}>
                              <span className={"label"}>House Design</span>
                              <span className={"separator"}>:</span>
                              <span className={"value"}>
                                {item.house_design}
                              </span>
                            </div>
                            <div className={"details-row"}>
                              <span className={"label"}>Facade</span>
                              <span className={"separator"}>:</span>
                              <span className={"value"}>
                                {item.facade_title}
                              </span>
                            </div>
                            <div className={"details-row"}>
                              <span className={"label"}>House Size</span>
                              <span className={"separator"}>:</span>
                              <span className={"value"}>
                                {item.total_area_m}m&sup2; ({item.total_area_sq}
                                sq)
                              </span>
                            </div>
                            <div className={"details-row"}>
                              <span className={"label"}>Lot Size</span>
                              <span className={"separator"}>:</span>
                              <span className={"value"}>
                                {item.lot_size}m&sup2;
                              </span>
                            </div>
                            <div className={"icons-row"}>
                              <div className={"icons-item"}>
                                <Icon name={IconName.BED} />
                                <span>{item.bedrooms}</span>
                              </div>
                              <div className={"icons-item"}>
                                <Icon name={IconName.COUCH} />
                                <span>{item.living_areas}</span>
                              </div>
                              <div className={"icons-item"}>
                                <Icon name={IconName.BATH} />
                                <span>{item.bathrooms}</span>
                              </div>
                              <div className={"icons-item"}>
                                <Icon name={IconName.CAR} />
                                <span>{item.car_spaces}</span>
                              </div>
                            </div>
                          </div>
                          <div className={"pricing"}>
                            <p className={"section-label"}>
                              {item.pdf_fixed_price ? "Fixed Price" : " "}
                            </p>
                            <p className={"price"}>
                              ${item.price.toLocaleString()}*
                            </p>

                            <button
                              className={"btn more-details"}
                              onClick={(e) => onClickMoreDetails(item, e)}
                            >
                              More Details
                            </button>
                          </div>
                        </div>
                      </CollapsableImageContent>
                      <div className={"more-details-mobile"}>
                        <MoreDetailsBtn
                          onClick={() => onClickMoreDetails(item)}
                        />
                      </div>
                    </>
                  </Selectable>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
      {windowSize.width < 992 && isModalOpen && (
        <FullscreenModal
          title={"Refine Your Search"}
          onClose={onCloseSearchModal}
          closing={isModalClosing}
        >
          <LandFilters
            suburbs={apiContext.regionResults}
            houseSizes={apiContext.houseSizesResults}
            suburb={apiContext.region ? apiContext.region.id : ""}
            estate={apiContext.estate ? apiContext.estate.id : ""}
            minMetres={
              apiContext.minLotSize !== null
                ? apiContext.minLotSize.toString(10)
                : ""
            }
            maxMetres={
              apiContext.maxLotSize !== null
                ? apiContext.maxLotSize.toString(10)
                : ""
            }
            onChangeSuburb={onChangeRegion}
            onChangeEstate={onChangeEstate}
            onChangeMinMetres={onChangeMinMetres}
            onChangeMaxMetres={onChangeMaxMetres}
            onReset={onResetFilters}
            onSearch={onLandFiltersSearch}
          />
        </FullscreenModal>
      )}
    </div>
  );
});

export default PackageSearchResultsScreen;
